import React from 'react'
import type { Answer } from 'types/answer'
import type { Page } from 'types/page'
import { type Question } from 'types/question'
import type { Response } from 'types/response'
import type { SessionObject } from 'types/session-object'

import useIntegration from '@/hooks/useIntegration'

import CalculationQuestion from './CalculationQuestion'
import DateQuestion from './DateQuestion'
import DividerQuestion from './DividerQuestion'
import DraggableFileUploadQuestion from './DraggableFileUploadQuestion'
import DropdownMultipleQuestion from './DropdownMultipleQuestion'
import DropdownQuestion from './DropdownQuestion'
import FileUploadQuestion from './FileUploadQuestion'
import HandoutQuestion from './HandoutQuestion.jsx'
import HorizontalCheckboxQuestionList from './HorizontalCheckboxQuestionList'
import HorizontalRadioListQuestion from './HorizontalRadioListQuestion'
import ImageQuestion from './ImageQuestion'
import InsuranceCarrierQuestion from './InsuranceCarrierQuestion'
import MarkdownQuestion from './MarkdownQuestion'
import MarkerQuestion from './MarkerQuestion'
import MatrixCheckboxQuestion from './MatrixCheckboxQuestion'
import MatrixOpenEndedQuestion from './MatrixOpenEndedQuestion'
import MatrixRadioQuestion from './MatrixRadioQuestion'
import MultiLineQuestion from './MultiLineQuestion'
import NameQuestion from './NameQuestion'
import NumberQuestion from './NumberQuestion'
import PhoneNumberQuestion from './PhoneNumberQuestion'
import ScaleQuestion from './ScaleQuestion'
import SignatureQuestion from './SignatureQuestion'
import SingleLineQuestion from './SingleLineQuestion'
import SSNQuestion from './SSNQuestion'
import StripeCardQuestion from './StripeCardQuestion'
import SummaryQuestion from './SummaryQuestion'
import TableOpenEndedQuestion from './TableOpenEndedQuestion'
import TimeQuestion from './TimeQuestion'
import VerticalCheckboxQuestionList from './VerticalCheckboxQuestionList'
import VerticalRadioListQuestion from './VerticalRadioListQuestion'

type Props = {
  page: Page
  question: Question
  isInternalForm?: boolean
  isQuestionReadonly: boolean
  willShowNotAllowedCursor: boolean
  answer: Answer | null
  isQuestionDirty: boolean
  isQuestionValid: boolean
  questionError?: string | undefined
  onAnswerChanged: (question: Question, answer: string) => undefined
  mode: undefined | 'client' | 'office' | 'read-only' | 'preview'
  response: Response
  sessions?: SessionObject
}

function UnknownQuestion() {
  return null
}

function QuestionFactory({
  page,
  question,
  isQuestionReadonly,
  willShowNotAllowedCursor,
  answer,
  isQuestionDirty,
  isInternalForm,
  isQuestionValid,
  questionError,
  onAnswerChanged,
  mode,
  response,
  sessions,
}: Props): React.ReactNode {
  const { integration } = useIntegration()
  const questionIntegration = integration
    ? question[`${integration}_integration`]
    : null
  const isOfficeUse = isInternalForm
    ? false
    : mode &&
      ['office', 'read-only', 'client'].includes(mode) &&
      question.is_office_use_only

  if (question.validation_type === 'ssn') {
    return (
      <SSNQuestion
        isOfficeUse={!!isOfficeUse}
        question={question}
        response={response}
        sessions={sessions}
      />
    )
  }

  if (question.question_type.question_type === 'radio') {
    if (question.question_type.question_subtype === 'vertical_list') {
      return (
        <VerticalRadioListQuestion
          isOfficeUse={!!isOfficeUse}
          mode={mode}
          question={question}
          response={response}
          sessions={sessions}
        />
      )
    }
    if (question.question_type.question_subtype === 'horizontal_list') {
      return (
        <HorizontalRadioListQuestion
          isOfficeUse={!!isOfficeUse}
          mode={mode}
          question={question}
          response={response}
          sessions={sessions}
        />
      )
    }
    if (question.question_type.question_subtype === 'dropdown') {
      return (
        <DropdownQuestion
          isOfficeUse={!!isOfficeUse}
          mode={mode}
          question={question}
          response={response}
          sessions={sessions}
        />
      )
    }
    if (question.question_type.question_subtype === 'dropdown_multiple') {
      return (
        <DropdownMultipleQuestion
          isOfficeUse={!!isOfficeUse}
          question={question}
          response={response}
          sessions={sessions}
        />
      )
    }
  }

  if (question.question_type.question_type === 'text') {
    if (
      [
        'patients.insurances.carrier|rank=primary',
        'patients.insurances.carrier|rank=secondary',
      ].includes(questionIntegration?.field ?? '')
    ) {
      return (
        <InsuranceCarrierQuestion
          isOfficeUse={!!isOfficeUse}
          question={question}
          response={response}
          sessions={sessions}
        />
      )
    }
    if (question.question_type.question_subtype === 'single_line') {
      return (
        <SingleLineQuestion
          answer={answer}
          isOfficeUse={!!isOfficeUse}
          isQuestionDirty={isQuestionDirty}
          isQuestionReadonly={isQuestionReadonly}
          isQuestionValid={isQuestionValid}
          mode={mode}
          onAnswerChanged={onAnswerChanged}
          question={question}
          questionError={questionError}
          response={response}
          sessions={sessions}
          willShowNotAllowedCursor={willShowNotAllowedCursor}
        />
      )
    }
    if (question.question_type.question_subtype === 'calculation') {
      return (
        <CalculationQuestion isOfficeUse={!!isOfficeUse} question={question} />
      )
    }
    if (question.question_type.question_subtype === 'multi_line') {
      return (
        <MultiLineQuestion
          isOfficeUse={!!isOfficeUse}
          mode={mode}
          question={question}
          response={response}
          sessions={sessions}
        />
      )
    }
    if (question.question_type.question_subtype === 'phone') {
      return (
        <PhoneNumberQuestion
          isOfficeUse={!!isOfficeUse}
          mode={mode}
          question={question}
          response={response}
          sessions={sessions}
        />
      )
    }
    if (question.question_type.question_subtype === 'email') {
      return (
        <SingleLineQuestion
          answer={answer}
          isOfficeUse={!!isOfficeUse}
          isQuestionDirty={isQuestionDirty}
          isQuestionReadonly={isQuestionReadonly}
          isQuestionValid={isQuestionValid}
          mode={mode}
          onAnswerChanged={onAnswerChanged}
          question={question}
          questionError={questionError}
          response={response}
          sessions={sessions}
          willShowNotAllowedCursor={willShowNotAllowedCursor}
        />
      )
    }
    if (question.question_type.question_subtype === 'full_name') {
      return (
        <NameQuestion
          isOfficeUse={!!isOfficeUse}
          mode={mode}
          question={question}
          response={response}
          sessions={sessions}
        />
      )
    }
  }

  if (question.question_type.question_type === 'checkbox') {
    if (question.question_type.question_subtype === 'vertical_list') {
      return (
        <VerticalCheckboxQuestionList
          isOfficeUse={!!isOfficeUse}
          mode={mode}
          question={question}
          response={response}
          sessions={sessions}
        />
      )
    }
    if (question.question_type.question_subtype === 'horizontal_list') {
      return (
        <HorizontalCheckboxQuestionList
          isOfficeUse={!!isOfficeUse}
          mode={mode}
          question={question}
          response={response}
          sessions={sessions}
        />
      )
    }
  }

  if (question.question_type.question_type === 'datetime') {
    if (
      question.question_type.question_subtype === 'date_only' ||
      question.question_type.question_subtype === 'month_year' ||
      question.question_type.question_subtype === 'datetime'
    ) {
      return (
        <DateQuestion
          isOfficeUse={!!isOfficeUse}
          mode={mode}
          question={question}
          response={response}
          sessions={sessions}
        />
      )
    }
    if (question.question_type.question_subtype === 'time_only') {
      return (
        <TimeQuestion
          isOfficeUse={!!isOfficeUse}
          mode={mode}
          question={question}
          response={response}
          sessions={sessions}
        />
      )
    }
  }

  if (question.question_type.question_type === 'number') {
    if (question.question_type.question_subtype === 'number') {
      return (
        <NumberQuestion
          isOfficeUse={!!isOfficeUse}
          mode={mode}
          question={question}
          response={response}
          sessions={sessions}
        />
      )
    }
    if (question.question_type.question_subtype === 'scale') {
      return (
        <ScaleQuestion
          question={question}
          response={response}
          sessions={sessions}
        />
      )
    }
  }

  if (question.question_type.question_type === 'file_upload') {
    if (question.question_type.question_subtype === 'file_upload') {
      if (question.multi_file_upload) {
        return (
          <DraggableFileUploadQuestion
            isOfficeUse={!!isOfficeUse}
            question={question}
          />
        )
      }
      return (
        <FileUploadQuestion isOfficeUse={!!isOfficeUse} question={question} />
      )
    }
    if (question.question_type.question_subtype === 'marker') {
      return <MarkerQuestion isOfficeUse={!!isOfficeUse} question={question} />
    }
    if (question.question_type.question_subtype === 'handout') {
      return <HandoutQuestion isOfficeUse={!!isOfficeUse} question={question} />
    }
  }

  if (question.question_type.question_type === 'signature') {
    return (
      <SignatureQuestion
        isOfficeUse={!!isOfficeUse}
        page={page}
        question={question}
      />
    )
  }

  if (question.question_type.question_type === 'matrix') {
    if (question.question_type.question_subtype === 'radio') {
      return (
        <MatrixRadioQuestion
          isOfficeUse={!!isOfficeUse}
          mode={mode}
          question={question}
          response={response}
          sessions={sessions}
        />
      )
    }
    if (question.question_type.question_subtype === 'checkbox') {
      return (
        <MatrixCheckboxQuestion
          isOfficeUse={!!isOfficeUse}
          mode={mode}
          question={question}
          response={response}
          sessions={sessions}
        />
      )
    }
    if (question.question_type.question_subtype === 'open_ended') {
      return (
        <MatrixOpenEndedQuestion
          isOfficeUse={!!isOfficeUse}
          mode={mode}
          question={question}
          response={response}
          sessions={sessions}
        />
      )
    }
  }

  if (question.question_type.question_type === 'display_only') {
    if (question.question_type.question_subtype === 'markdown') {
      return <MarkdownQuestion question={question} />
    }
    if (question.question_type.question_subtype === 'image') {
      return <ImageQuestion question={question} />
    }
  }

  if (question.question_type.question_type === 'table') {
    if (question.question_type.question_subtype === 'open_ended') {
      return (
        <TableOpenEndedQuestion
          isOfficeUse={!!isOfficeUse}
          mode={mode}
          question={question}
          response={response}
          sessions={sessions}
        />
      )
    }
  }

  if (question.question_type.question_type === 'credit_card') {
    if (question.question_type.question_subtype === 'stripe') {
      return <StripeCardQuestion question={question} response={response} />
    }
  }

  if (question.question_type.question_type === 'summary') {
    return <SummaryQuestion {...{ question, answer }} />
  }

  if (question.question_type.question_type === 'divider') {
    return <DividerQuestion />
  }

  return <UnknownQuestion />
}

export default QuestionFactory
